import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { MinifiedDispensary } from '@gcv/shared';
import { DueDiligenceStatus, IndustryType } from '@gcv/shared';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';

import { BanksApi } from 'api';
import { ReactComponent as HelpIcon } from 'assets/images/ic-help.svg';
import { DUE_DILIGENCE_TO_ONBOARDING_STATUS, OnboardingStatus } from 'domain/consts/onboarding-status';
import { TEMPLATE_RESULT_KEY } from 'hooks/keys';
import { useBank } from 'hooks/queries';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import type { SelectOption } from 'ui';
import { Button, Dialog, Hyperlink, InputRadio, InputSelect, InputTextArea, Text, Title, Tooltip } from 'ui';
import { updateMinifiedDispensariesStatus } from 'ui/apps/fi/util';
import { infoBoxStyles, warningBoxStyles } from 'ui/theme';
import palette from 'ui/theme/palette';
import { queryClient } from 'util/queryClient';
import { actions } from '../actions';

interface Props extends Record<string, unknown> {
  isOpen: boolean;
  onClose: () => void;
  selectedAccounts: string[];
  initialStatus: DueDiligenceStatus;
  additionalOnSubmit?: () => void;
}

export const ChangeStatusModal: React.FC<Props> = (props) => {
  const banksApi = new BanksApi();
  const snackbarStore = getSnackbarStore();
  const fiDispensaryStore = getFiDispensaryStore();

  const { bank } = useBank();
  const [newStatus, setNewStatus] = useState<DueDiligenceStatus>();
  const [sendNotification, setSendNotification] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAccounts, setShowAccounts] = useState<boolean>(false);
  const form = useForm();

  const getStatusOptions = () => {
    const options: SelectOption[] = [
      { label: OnboardingStatus.Active, value: DueDiligenceStatus.BANK_APPROVED },
      { label: OnboardingStatus.Reviewing, value: DueDiligenceStatus.BANK_REVIEW_IN_PROGRESS },
      { label: OnboardingStatus.Awaiting, value: DueDiligenceStatus.BANK_AWAITING_REVIEW },
      { label: OnboardingStatus.Invited, value: DueDiligenceStatus.BANK_PENDING }
    ];

    const disabledOption = options.find(
      (o) => o.label === DUE_DILIGENCE_TO_ONBOARDING_STATUS[props.initialStatus]
    );
    if (disabledOption) {
      disabledOption.disabled = true;
      disabledOption.label = disabledOption.label + ' (current)';
    }

    return options;
  };

  const onSubmit = async (data: FieldValues) => {
    try {
      setIsLoading(true);
      const createSar = data.createInitialSar === 'yes';
      const options = {
        businessType: data.business_type,
        createInitialSar: newStatus === DueDiligenceStatus.BANK_APPROVED && createSar,
        sendNotification: newStatus === DueDiligenceStatus.BANK_PENDING && sendNotification,
        sendNotificationNote:
          newStatus === DueDiligenceStatus.BANK_PENDING && sendNotification
            ? data.sendNotificationNote
            : undefined
      };
      await banksApi.bulkChangeDueDiligenceStatus(bank!.id, props.selectedAccounts, data.status, options);
      props.selectedAccounts.forEach((crbId) => {
        const disp = fiDispensaryStore.updateDispensaryAssignedTemplate(crbId, { newStatus: data.status });
        queryClient.invalidateQueries({
          queryKey: [
            TEMPLATE_RESULT_KEY,
            crbId,
            disp?.assigned_onboarding_template.template_id,
            disp?.assigned_onboarding_template.template_result_id
          ]
        });
      });
      updateMinifiedDispensariesStatus(bank!.id, props.selectedAccounts, data.status);
      actions.sortDispensaries(fiDispensaryStore.dispensaries);
      snackbarStore.showSuccessSnackbarMessage('Statuses changed successfully');
      if (props.additionalOnSubmit) {
        props.additionalOnSubmit();
      }
    } catch (e) {
      snackbarStore.showErrorSnackbarMessage(
        'There was an issue updating the status of some accounts. Please reach out to support'
      );
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onClose = async () => {
    setNewStatus(undefined);
    setSendNotification(false);
    setShowAccounts(false);
    form.reset({});
    props.onClose();
  };

  const accountsWithoutBusinessType = props.selectedAccounts
    .filter((crbId) => {
      const crb = fiDispensaryStore.findDispensary(crbId) as MinifiedDispensary;
      return !crb?.business_type && crb.industryType === IndustryType.CANNABIS;
    })
    .map((crbId) => {
      return fiDispensaryStore.findDispensary(crbId)?.name;
    });
  const needsBusinessType =
    accountsWithoutBusinessType.length > 0 &&
    DUE_DILIGENCE_TO_ONBOARDING_STATUS[props.initialStatus] === OnboardingStatus.Invited;

  return (
    <Dialog
      isOpen={props.isOpen}
      handleClose={onClose}
      title={
        newStatus === DueDiligenceStatus.BANK_APPROVED ? (
          <Tooltip
            title={`Approving marks every outstanding document and due diligence requirement on the selected account(s) as reviewed by you, and records your name and today's date against each one.`}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Title
                content={`Approve Account${accountsWithoutBusinessType.length === 1 ? '' : 's'}`}
                justify="left"
              />
              <HelpIcon />
            </Box>
          </Tooltip>
        ) : (
          'Change Status'
        )
      }
      dataCy={'change-status-modal'}
      action={
        <Button
          useMinWidth
          label={'Save'}
          color="primary"
          disabled={!form.formState.isValid}
          isLoading={isLoading}
          onClick={form.handleSubmit((data) => onSubmit(data))}
          dataCy="change-status-save-button"
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text
          content={`Choose a status for the ${props.selectedAccounts.length} selected account${
            props.selectedAccounts.length > 1 ? 's' : ''
          }.`}
        />
        <InputSelect
          label={'New Status'}
          name={'status'}
          options={getStatusOptions()}
          rules={{
            required: {
              value: true,
              message: 'is required'
            }
          }}
          placeholder="Select a status"
          additionalOnChange={(e) => setNewStatus(e.target.value)}
          {...form}
        />
        {newStatus === DueDiligenceStatus.BANK_PENDING && (
          <>
            <InputRadio
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' }
              ]}
              label={'Send Notification?'}
              name={'sendNotification'}
              onChange={(val) => setSendNotification(val === 'yes' ? true : false)}
              justifyContent="row"
              rules={{
                required: {
                  value: true,
                  message: 'is required'
                }
              }}
              {...form}
            />
            {sendNotification && (
              <>
                <InputTextArea
                  label={'Add a Note (optional)'}
                  name={'sendNotificationNote'}
                  placeholder="Add a note to the business that will be included in the email to Account Owners prompting them to fill out their new application."
                  {...form}
                />
                <Text content={`This note is included in the email sent to Account Owners`} type="body3" />
              </>
            )}
          </>
        )}
        {needsBusinessType && (
          <>
            <Box sx={warningBoxStyles}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1
                  }}
                >
                  <InfoOutlined sx={{ fontSize: '16px', color: palette.warning.text }} />
                  <Text
                    content={`${accountsWithoutBusinessType.length} selected account${
                      accountsWithoutBusinessType.length === 1 ? ' is' : 's are'
                    } missing a business type`}
                    type="body2"
                  />
                </Box>
                <Hyperlink
                  label={`${showAccounts ? 'Hide' : 'Show'} Accounts`}
                  onClick={() => {
                    setShowAccounts(!showAccounts);
                  }}
                  type="body3"
                />
                {showAccounts && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {accountsWithoutBusinessType.map((a, index) => {
                      return <Text content={a} type="body3" key={index} />;
                    })}
                  </Box>
                )}
              </Box>
            </Box>
            <InputSelect
              label={`Business Type for ${accountsWithoutBusinessType.length} Account${
                accountsWithoutBusinessType.length === 1 ? '' : 's'
              }`}
              name={'business_type'}
              options={[
                { label: 'Retail', value: 'retail' },
                { label: 'Wholesale', value: 'wholesale' }
              ]}
              rules={{
                required: {
                  value: true,
                  message: 'is required'
                }
              }}
              placeholder="Select a business type"
              {...form}
            />
            <Text
              content={`Applies only to the account${
                accountsWithoutBusinessType.length === 1 ? '' : 's'
              } listed above. Any accounts that already have a business type will remain unchanged.`}
              type="body3"
            />
          </>
        )}
        {newStatus === DueDiligenceStatus.BANK_APPROVED && (
          <>
            <InputRadio
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' }
              ]}
              label={'Create Initial SAR?'}
              name={'createInitialSar'}
              justifyContent="row"
              tooltip="If Yes, an initial SAR will be created for any selected account(s) which do not already have an open or completed initial SAR."
              rules={{
                required: {
                  value: true,
                  message: 'is required'
                }
              }}
              {...form}
            />
            <Box sx={infoBoxStyles}>
              <InfoOutlined sx={{ fontSize: '16px', color: palette.secondary.main }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Text
                  content={`Account numbers and monitoring review frequencies are configured per account in each account's Internal tab`}
                  type="body2"
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};
